const API_BASE = "https://openrouter.ai/api/v1";

function getKey(): string {
  const key = import.meta.env.VITE_OPENROUTER_KEY;
  if (!key) throw new Error("VITE_OPENROUTER_KEY not set in .env");
  return key as string;
}

async function fetchOrThrow(url: string, opts: RequestInit): Promise<any> {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const text = await res.text().catch(() => "unknown error");
    throw new Error(`OpenRouter ${res.status}: ${text}`);
  }
  return res.json();
}

/** Step 1: Transcribe audio Base64 → text via Whisper */
export async function transcribeAudio(audioBase64: string, mimeType: string): Promise<string> {
  const format = mimeType.split("/")[1]?.replace("webm", "webm") || "webm";

  const json = await fetchOrThrow(`${API_BASE}/audio/transcriptions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getKey()}`,
    },
    body: JSON.stringify({
      model: "openai/whisper-large-v3",
      input_audio: {
        data: audioBase64,
        format,
      },
    }),
  });
  return json.text ?? "";
}

/** Step 2: Structure transcribed text → InventoryRow[] via Claude */
export async function structureWithClaude(transcript: string): Promise<{ items: { item: string; quantity: number; unit: string; unit_price: number; total: number }[] }> {
  const systemPrompt = `You are a data extraction assistant for a paint manufacturing company in Nigeria. Convert the transcribed voice notes into a structured JSON array representing inventory items.

Rules:
1. All currency is Nigerian Naira (₦). "15k" means 15000.
2. Match items to common paint raw materials, but allow flexibility for new items not explicitly known.
3. Output ONLY valid JSON. No markdown, no code fences, no explanation.
4. If a unit_price is missing or not spoken, leave it as 0.
5. Calculate the 'total' if quantity and unit_price are provided. If not, set 'total' to 0.
6. Use this exact structure: { "items": [ { "item": string, "quantity": number, "unit": string, "unit_price": number, "total": number } ] }`;

  const json = await fetchOrThrow(`${API_BASE}/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getKey()}`,
    },
    body: JSON.stringify({
      model: "anthropic/claude-sonnet-4",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: transcript },
      ],
      temperature: 0.1,
      max_tokens: 2000,
      response_format: { type: "json_object" },
    }),
  });

  const content: string = json.choices?.[0]?.message?.content ?? "";
  const cleaned = content.trim();

  try {
    return JSON.parse(cleaned);
  } catch (e) {
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) {
      try { return JSON.parse(match[0]); } catch {}
    }
    console.error("[structureWithClaude] Raw response:", content.slice(0, 500));
    throw new Error(`Failed to parse Claude response as JSON: ${(e as Error).message}`);
  }
}

